import type { match } from 'react-router-dom'
import { Col, Row, Card, Skeleton } from 'antd'
import { Flex, Heading } from '@weareberyl/design-system'
import { useAddonSummaryFragment } from '~/gql/generated/graphql'

import HeadTitle from '~/components/HeadTitle'
import { useSchemeAddon } from './data'
import { AddonPageHeader, AddonTypeTag } from '../components'
import CardSection from '~/components/shared/CardSection'
import { formatDuration } from '../../Product/utils'

export const AddonPricingInfoPanels = ({ id }: { id: string }) => {
  const { complete, data } = useAddonSummaryFragment({ id })

  if (!complete) {
    return null
  }

  return (
    <>
      <Card
        title={
          <Flex justifyContent="space-between">
            <Heading flex={1}>
              {data.name}
              {data.is_active ? '' : ' (inactive)'}
            </Heading>
            <AddonTypeTag addonType={data.addon_type} />
          </Flex>
        }
      >
        <Flex gridGap={40}>
          <CardSection
            maxWidth={300}
            items={[
              { key: 'Price', value: data.price.formatted_amount },
              {
                key: 'Discoverable',
                value: data.is_discoverable ? 'Discoverable' : 'Hidden',
              },
              {
                key: 'Protected',
                value: data.is_protected ? 'Protected' : 'Public',
              },
              {
                key: 'Active',
                value: data.is_active ? 'Active' : 'Inactive',
              },
              data.is_protected
                ? {
                    key: 'Role allowlist',
                    value: data.role_whitelist.join(','),
                  }
                : undefined,
            ]}
          />
          <CardSection
            maxWidth={300}
            items={[
              {
                key: 'Valid for',
                value: formatDuration(data.validity_interval) ?? '-',
              },
            ]}
          />
        </Flex>
      </Card>
    </>
  )
}

type Props = {
  match: match<{ id: string; scheme_id: string }>
}

const AddonDetail = ({ match }: Props) => {
  const schemeAddonId = match.params.id
  const schemeId = match.params.scheme_id
  const { loading, data } = useSchemeAddon({ schemeAddonId })

  return (
    <>
      <HeadTitle pageTitle={data?.scheme_addon?.name ?? '...'} />
      <AddonPageHeader id={schemeAddonId} schemeId={schemeId} goBack />

      <Skeleton loading={loading} active>
        {data?.scheme_addon && (
          <Row gutter={16}>
            <Col xs={24} lg={16} className="flex-col">
              <AddonPricingInfoPanels id={schemeAddonId} />
            </Col>
          </Row>
        )}
      </Skeleton>
    </>
  )
}

export default AddonDetail
